/** @format */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-date-range';
import { startOfDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useTranslation } from 'react-i18next';
import { enUS, de, fr, es, it, pl, deAT, enGB, cs } from 'date-fns/locale';

const RANGE_KEY = 'selection';
const PICKER_EDGE = '#5cb8e6';
const PICKER_FILL = '#d1effc';

function startOfDaySafe(value) {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return startOfDay(new Date());
  }
  return startOfDay(value);
}

function normalizeInitialRange(initialRange) {
  const today = startOfDay(new Date());
  const start = initialRange?.startDate ? startOfDaySafe(initialRange.startDate) : today;
  const end = initialRange?.endDate ? startOfDaySafe(initialRange.endDate) : today;
  return {
    startDate: start,
    endDate: end,
    key: RANGE_KEY,
    color: PICKER_EDGE
  };
}

export default function ExtranetTimeframeCalendarDialog({ open, initialRange, onDismiss, onConfirm, onClear }) {
  const { t, i18n } = useTranslation();
  const initialSig = useMemo(() => {
    if (!initialRange?.startDate || !initialRange?.endDate) return 'default';
    return `${initialRange.startDate.getTime()}_${initialRange.endDate.getTime()}`;
  }, [initialRange?.startDate?.getTime?.(), initialRange?.endDate?.getTime?.()]);

  const [ranges, setRanges] = useState(() => [
    normalizeInitialRange(initialRange?.startDate && initialRange?.endDate ? initialRange : null)
  ]);

  const locale = useMemo(() => {
    return i18n.language;
  }, [i18n.language]);

  const dateRangeLocale = useMemo(() => {
    switch (locale) {
      case 'en':
        return enUS;
      case 'fr':
        return fr;
      case 'de':
        return de;
      case 'es':
        return es;
      case 'it':
        return it;
      case 'pl':
        return pl;
      case 'de-AT':
        return deAT;
      case 'en-GB':
        return enGB;
      case 'cs':
        return cs;
      case 'de-CH':
        return de;
      default:
        return enUS;
    }
  }, [locale]);

  useEffect(() => {
    if (!open) return;
    setRanges([normalizeInitialRange(initialRange?.startDate && initialRange?.endDate ? initialRange : null)]);
  }, [open, initialSig]);

  const handleChange = useCallback(item => {
    const next = item.selection;
    if (next?.startDate != null && next?.endDate != null) {
      setRanges([{ ...next, key: RANGE_KEY, color: PICKER_EDGE }]);
    }
  }, []);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  const handleConfirm = useCallback(() => {
    const r = ranges[0];
    if (r?.startDate && r?.endDate) {
      onConfirm({ startDate: r.startDate, endDate: r.endDate });
    }
  }, [ranges, onConfirm]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="extranet-timeframe__panel extranet-timeframe__panel--range"
      role="dialog"
      aria-modal="false"
      aria-label="Choose timeframe date range">
      <div className="extranet-timeframe__panel-body">
        <DateRange
          className="extranet-timeframe__calendar"
          ranges={ranges}
          onChange={handleChange}
          moveRangeOnFirstSelection={false}
          retainEndDateOnFirstSelection={false}
          showDateDisplay={false}
          showMonthArrow
          showMonthAndYearPickers
          monthDisplayFormat="MMMM yyyy"
          weekdayDisplayFormat="EEEEE"
          weekStartsOn={1}
          locale={dateRangeLocale}
          dragSelectionEnabled
          editableDateInputs={false}
          direction="vertical"
          months={1}
          color={PICKER_EDGE}
          rangeColors={[PICKER_EDGE, PICKER_FILL]}
          minDate={new Date(1900, 0, 1)}
          maxDate={new Date(2100, 11, 31)}
        />
      </div>
      <div className="extranet-timeframe__panel-footer">
        <button type="button" className="extranet-timeframe__action" onClick={onClear}>
          {t('administrationPage.extranetContents.clear')}
        </button>
        <button type="button" className="extranet-timeframe__action" onClick={onDismiss}>
          {t('common.button.cancel')}
        </button>
        <button type="button" className="extranet-timeframe__action" onClick={handleConfirm}>
          {t('common.button.ok')}
        </button>
      </div>
    </div>
  );
}
